import { Component } from '@angular/core';

import { MessageService } from '../services/message.service.js';

interface PopupExample {
  id: string;
  title: string;
  description: string;
  location: string;
  trigger: () => void;
}

interface PopupCategory {
  category: string;
  icon: string;
  count: number;
  examples: PopupExample[];
}

@Component({
    selector: 'app-popup-showcase',
    imports: [],
    templateUrl: './popup-showcase.component.html',
    styleUrls: ['./popup-showcase.component.scss']
})
export class PopupShowcaseComponent {
  
  constructor(private messageService: MessageService) {}
  
  popupCategories: PopupCategory[] = [
    {
      category: 'Retry & Connection Errors',
      icon: '🔄',
      count: 3,
      examples: [
        {
          id: 'retry-1',
          title: '✅ STANDARDIZED Retry Error (All Services)',
          description: 'Used for ALL retry failures - Gemini, Azure, XAI streaming after 3 attempts',
          location: 'message.service.ts:showRetryError()',
          trigger: () => {
            this.messageService.showRetryError();
          }
        },
        {
          id: 'retry-2',
          title: '✅ STANDARDIZED Connection Issue',
          description: 'Used for connection issues during streaming',
          location: 'message.service.ts:showConnectionIssue()',
          trigger: () => {
            this.messageService.showConnectionIssue();
          }
        },
        {
          id: 'retry-3',
          title: '✅ STANDARDIZED Unexpected Error',
          description: 'Used for all unexpected errors (replaces "Something went wrong")',
          location: 'message.service.ts:showUnexpectedError()',
          trigger: () => {
            this.messageService.showUnexpectedError();
          }
        }
      ]
    },
    {
      category: 'Token Limit Errors',
      icon: '🎫',
      count: 5,
      examples: [
        {
          id: 'token-1',
          title: 'Gemini Token Limit (Estimated)',
          description: 'When model doesn\'t support countTokens and estimated tokens exceed limit',
          location: 'reference.component.ts:689',
          trigger: () => {
            this.messageService.showTokenLimitExceeded({
              model: 'gemini-1.5-pro',
              tokensNeeded: 15000,
              tokensRemaining: 10000,
              tokensShort: 5000,
              estimatedCharactersToReduce: 20000,
              isEstimated: true
            });
          }
        },
        {
          id: 'token-2',
          title: 'Gemini Token Limit (Actual Count)',
          description: 'When actual token count exceeds remaining limit',
          location: 'reference.component.ts:788',
          trigger: () => {
            this.messageService.showTokenLimitExceeded({
              model: 'gemini-1.5-flash',
              tokensNeeded: 18500,
              tokensRemaining: 12000,
              tokensShort: 6500,
              estimatedCharactersToReduce: 26000,
              isEstimated: false
            });
          }
        },
        {
          id: 'token-3',
          title: 'GPT Token Limit',
          description: 'GPT model token limit check before send',
          location: 'reference.component.ts:1070',
          trigger: () => {
            this.messageService.showTokenLimitExceeded({
              model: 'gpt-4',
              tokensNeeded: 8500,
              tokensRemaining: 5000,
              tokensShort: 3500,
              estimatedCharactersToReduce: 14000,
              isEstimated: false
            });
          }
        },
        {
          id: 'token-4',
          title: 'Gemini Token Limit (Fallback)',
          description: 'Fallback when countTokens fails',
          location: 'reference.component.ts:788 (catch)',
          trigger: () => {
            this.messageService.showTokenLimitExceeded({
              model: 'gemini-pro',
              tokensNeeded: 22000,
              tokensRemaining: 15000,
              tokensShort: 7000,
              estimatedCharactersToReduce: 28000,
              isEstimated: true
            });
          }
        },
        {
          id: 'token-5',
          title: 'Gemini Streaming Error (Token Related)',
          description: 'Error in Gemini streaming after request completes',
          location: 'reference.component.ts:1878',
          trigger: () => {
            this.messageService.showError('An error occurred with Gemini. Please try a shorter prompt or clear chat.');
          }
        }
      ]
    },
    {
      category: 'File Validation Errors',
      icon: '📁',
      count: 4,
      examples: [
        {
          id: 'file-1',
          title: 'Invalid File Type',
          description: 'When dropped files have unsupported types',
          location: 'reference.component.ts:1559',
          trigger: () => {
            this.messageService.showInvalidFileTypes(['document.exe', 'malware.bat', 'script.sh']);
          }
        },
        {
          id: 'file-2',
          title: 'File Size Limit Exceeded (Drop)',
          description: 'Total file size exceeds model limits in drag-drop',
          location: 'reference.component.ts:1574',
          trigger: () => {
            this.messageService.showFileSizeExceeded('gemini-1.5-pro', 20);
          }
        },
        {
          id: 'file-3',
          title: 'File Size Limit Exceeded (Select)',
          description: 'Combined file size exceeds limits in file selection',
          location: 'reference.component.ts:1611',
          trigger: () => {
            this.messageService.showFileSizeExceeded('GPT-4', 10);
          }
        },
        {
          id: 'file-4',
          title: 'Unsupported File Type',
          description: 'File type not recognized during processing',
          location: 'reference.component.ts:1701',
          trigger: () => {
            this.messageService.showUnsupportedFileType();
          }
        }
      ]
    },
    {
      category: 'Session Management',
      icon: '💬',
      count: 4,
      examples: [
        {
          id: 'session-1',
          title: 'Session Delete Confirmation',
          description: 'User clicks delete on a chat session',
          location: 'sidebar.component.ts:304',
          trigger: () => {
            this.messageService.showDeleteSessionConfirmation();
          }
        },
        {
          id: 'session-2',
          title: 'Session Deleted Success',
          description: 'After successful session deletion',
          location: 'sidebar.component.ts:415',
          trigger: () => {
            this.messageService.showSessionDeleted('My Awesome Chat');
          }
        },
        {
          id: 'session-3',
          title: 'Session Pin/Unpin Toast',
          description: 'User pins or unpins a chat session',
          location: 'sidebar.component.ts:394',
          trigger: () => {
            this.messageService.showSessionPinned(true);
          }
        },
        {
          id: 'session-4',
          title: 'Session Deletion API Error',
          description: 'API error when deleting session from server',
          location: 'sidebar.component.ts:466',
          trigger: () => {
            this.messageService.showSessionDeleteError();
          }
        }
      ]
    },
    {
      category: 'User Validation Errors',
      icon: '👤',
      count: 4,
      examples: [
        {
          id: 'user-1',
          title: 'User ID Not Found (Session Save)',
          description: 'Cannot save session because userId missing',
          location: 'reference.component.ts:2475',
          trigger: () => {
            this.messageService.showUserIdNotFound('save');
          }
        },
        {
          id: 'user-2',
          title: 'Session Save API Error',
          description: 'API error when saving chat session',
          location: 'reference.component.ts:2563',
          trigger: () => {
            this.messageService.showSessionSaveError();
          }
        },
        {
          id: 'user-3',
          title: 'User ID Not Found (Session Delete)',
          description: 'Cannot delete session because userId missing',
          location: 'reference.component.ts:2913',
          trigger: () => {
            this.messageService.showUserIdNotFound('delete');
          }
        },
        {
          id: 'user-4',
          title: 'Session Delete API Error',
          description: 'API error when deleting session',
          location: 'reference.component.ts:2985',
          trigger: () => {
            this.messageService.showSessionDeleteApiError();
          }
        }
      ]
    },
    {
      category: 'Model Changes & Confirmations',
      icon: '🤖',
      count: 3,
      examples: [
        {
          id: 'model-1',
          title: 'GPT-5 Mini Switch (New Context)',
          description: 'Switching to GPT-5 Mini in new chat context',
          location: 'reference.component.ts:3188',
          trigger: () => {
            this.messageService.showModelSwitchConfirmation('GPT-5 Mini', true, false);
          }
        },
        {
          id: 'model-2',
          title: 'Model Switch (GPT-5 Mini with reasoning)',
          description: 'Switching model to GPT-5 Mini in existing chat',
          location: 'reference.component.ts:3263',
          trigger: () => {
            this.messageService.showModelSwitchConfirmation('GPT-5 Mini', true, true);
          }
        },
        {
          id: 'model-3',
          title: 'Model Switch (Other models)',
          description: 'Switching to any non-GPT-5-Mini model',
          location: 'reference.component.ts:3263 (else)',
          trigger: () => {
            this.messageService.showModelSwitchConfirmation('GPT-4 Turbo', false, true);
          }
        }
      ]
    },
    {
      category: 'Voice Input Errors',
      icon: '🎤',
      count: 3,
      examples: [
        {
          id: 'voice-1',
          title: 'Voice Input Not Supported',
          description: 'Browser doesn\'t support speech recognition',
          location: 'reference.component.ts:3123',
          trigger: () => {
            this.messageService.showVoiceNotSupported();
          }
        },
        {
          id: 'voice-2',
          title: 'No Speech Detected',
          description: 'Speech recognition error - no speech',
          location: 'reference.component.ts:3387',
          trigger: () => {
            this.messageService.showVoiceRecognitionError('no-speech');
          }
        },
        {
          id: 'voice-3',
          title: 'Microphone Permission Denied',
          description: 'User denied microphone access',
          location: 'reference.component.ts:3387',
          trigger: () => {
            this.messageService.showVoiceRecognitionError('not-allowed');
          }
        }
      ]
    },
    {
      category: 'Feedback System',
      icon: '💬',
      count: 3,
      examples: [
        {
          id: 'feedback-1',
          title: 'Feedback Missing Information',
          description: 'User tries to submit empty feedback',
          location: 'feedback-popup.component.ts:25',
          trigger: () => {
            this.messageService.showFeedbackMissing();
          }
        },
        {
          id: 'feedback-2',
          title: 'Feedback Submit Success',
          description: 'Successful feedback submission',
          location: 'feedback-popup.component.ts:47',
          trigger: () => {
            this.messageService.showFeedbackSuccess();
          }
        },
        {
          id: 'feedback-3',
          title: 'Feedback Submit Error',
          description: 'API error when submitting feedback',
          location: 'feedback-popup.component.ts:60',
          trigger: () => {
            this.messageService.showFeedbackError();
          }
        }
      ]
    },
    {
      category: 'Image Generation',
      icon: '🖼️',
      count: 1,
      examples: [
        {
          id: 'image-1',
          title: 'Image Generation Failed',
          description: 'DALL-E/GPT image generation fails',
          location: 'reference.component.ts:3567',
          trigger: () => {
            this.messageService.showImageGenerationError('Unable to generate image. Please try again.');
          }
        }
      ]
    }
  ];

  get totalPopups(): number {
    return this.popupCategories.reduce((sum, cat) => sum + cat.count, 0);
  }

  triggerPopup(example: PopupExample): void {
    example.trigger();
  }

  triggerAllInCategory(category: PopupCategory): void {
    let index = 0;
    const triggerNext = () => {
      if (index < category.examples.length) {
        category.examples[index].trigger();
        index++;
        setTimeout(triggerNext, 500);
      }
    };
    triggerNext();
  }
}
